import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface NewsDetail {
  id: number;
  title: string;
  date: string;
  content: string;
  category: string;
  country: string;
  industry?: string;
}

const NewsDetailEn: React.FC = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [newsDetail, setNewsDetail] = useState<NewsDetail | null>(null);
  const location = useLocation();

  useEffect(() => {
    // Get news ID from URL parameters or state
    const searchParams = new URLSearchParams(location.search);
    const newsId = searchParams.get('id') || location.state?.newsId;
    
    // Mock news detail data
    const newsData: { [key: string]: NewsDetail } = {
      '1': {
        id: 1,
        title: '[Indonesia] Economic Trends | 2023 GDP $1.42 Trillion, Digital Economy Growing 22% Annually with Future Growth Opportunities',
        date: '2025-05-21 15:15:34',
        category: 'Country Insights',
        country: 'Indonesia',
        content: `
          <div class="prose max-w-none">
            <h2 class="text-2xl font-bold mb-4">Indonesia Economic Overview</h2>
            <p class="mb-4 leading-relaxed">Indonesia, as Southeast Asia's largest economy, achieved a GDP of $1.42 trillion in 2023, demonstrating strong economic growth momentum. The digital economy, as an emerging growth point, achieved an annual growth rate of 22%, becoming an important engine driving Indonesia's economic transformation.</p>
            
            <h3 class="text-xl font-semibold mb-3">Current State of Digital Economy Development</h3>
            <p class="mb-4 leading-relaxed">The rapid development of Indonesia's digital economy is attributed to several key factors:</p>
            <ul class="list-disc pl-6 mb-4 space-y-2">
              <li>Massive population base and young demographic structure</li>
              <li>Rapid increase in mobile internet penetration</li>
              <li>Government policy support for digital transformation</li>
              <li>Flourishing development of e-commerce platforms and fintech</li>
            </ul>
            
            <h3 class="text-xl font-semibold mb-3">Major Growth Areas</h3>
            <p class="mb-4 leading-relaxed">The main growth areas of Indonesia's digital economy include:</p>
            <div class="grid md:grid-cols-2 gap-4 mb-6">
              <div class="bg-blue-50 p-4 rounded-lg">
                <h4 class="font-semibold text-blue-800 mb-2">E-commerce</h4>
                <p class="text-sm text-blue-700">Online retail market continues to expand, expected to reach $50 billion by 2025</p>
              </div>
              <div class="bg-green-50 p-4 rounded-lg">
                <h4 class="font-semibold text-green-800 mb-2">Fintech</h4>
                <p class="text-sm text-green-700">Digital payment and online lending services developing rapidly, user growth exceeding 30% annually</p>
              </div>
              <div class="bg-purple-50 p-4 rounded-lg">
                <h4 class="font-semibold text-purple-800 mb-2">Online Education</h4>
                <p class="text-sm text-purple-700">Pandemic-driven surge in online education demand, market size growing at 40% annually</p>
              </div>
              <div class="bg-orange-50 p-4 rounded-lg">
                <h4 class="font-semibold text-orange-800 mb-2">Digital Entertainment</h4>
                <p class="text-sm text-orange-700">Gaming, video streaming and other digital entertainment industries flourishing</p>
              </div>
            </div>
            
            <h3 class="text-xl font-semibold mb-3">Future Development Opportunities</h3>
            <p class="mb-4 leading-relaxed">Looking ahead, Indonesia's digital economy still has enormous development potential:</p>
            <ol class="list-decimal pl-6 mb-4 space-y-2">
              <li><strong>Infrastructure Development:</strong> Improvement of 5G networks and digital infrastructure will further drive digital economy development</li>
              <li><strong>Talent Development:</strong> Increased investment in digital skills training and education will provide more professional talent</li>
              <li><strong>Policy Support:</strong> Government continues to introduce policies favorable to digital economy development</li>
              <li><strong>Regional Cooperation:</strong> Digital economy cooperation with other ASEAN countries will create larger market space</li>
            </ol>
            
            <div class="bg-gray-100 p-6 rounded-lg mb-6">
              <h4 class="font-semibold mb-3">Expert Opinion</h4>
              <blockquote class="italic text-gray-700">
                "The rapid development of Indonesia's digital economy is not only reflected in numerical growth, but more importantly, it is changing traditional business models, providing more development opportunities for SMEs, while also bringing new investment opportunities for foreign investors."
              </blockquote>
              <cite class="text-sm text-gray-600 mt-2 block">— Senior Researcher, Indonesian Economic Research Institute</cite>
            </div>
            
            <h3 class="text-xl font-semibold mb-3">Investment Recommendations</h3>
            <p class="mb-4 leading-relaxed">For investors interested in entering the Indonesian market, we recommend focusing on the following areas:</p>
            <ul class="list-disc pl-6 mb-4 space-y-2">
              <li>Fintech and digital payment solutions</li>
              <li>E-commerce platforms and logistics delivery services</li>
              <li>Online education and vocational training platforms</li>
              <li>Smart city and digital government solutions</li>
            </ul>
            
            <p class="text-gray-600 text-sm mt-8 pt-4 border-t">
              Data sources: Statistics Indonesia, Bank Indonesia, Southeast Asia Digital Economy Report 2023
            </p>
          </div>
        `
      },
      '2': {
        id: 2,
        title: '[Indonesia] Demographics & Culture | Decoding Business Secrets: Religious Beliefs, Festival Economy & Localization Strategy Analysis',
        date: '2025-05-12 17:03:53',
        category: 'Country Insights',
        country: 'Indonesia',
        content: `
          <div class="prose max-w-none">
            <div class="bg-blue-50 p-6 rounded-lg mb-6">
              <h2 class="text-2xl font-bold mb-4 text-blue-900">Key Insights</h2>
              <p class="text-blue-800 leading-relaxed">As Southeast Asia's largest economy, Indonesia has become a key market for global enterprises expanding into Southeast Asia with its massive population base, abundant natural resources, and growing middle-class consumption power. However, the complex religious and cultural network behind this market presents both opportunities and challenges. This article provides systematic insights and strategic recommendations for enterprises planning to enter the Indonesian market from three dimensions: religion, culture, and business.</p>
            </div>

            <h2 class="text-2xl font-bold mb-4">Religious Beliefs: The Foundation of Diversity</h2>
            <div class="mb-6">
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Islam (87%)</h3>
              <p class="mb-4 leading-relaxed">Indonesia is the world's most populous Muslim country, with Muslims accounting for 87% of the total population, with Sunni Muslims comprising the vast majority. Religious beliefs profoundly influence Indonesians' daily lives, consumption habits, and business decisions. Enterprises must fully consider religious factors when formulating marketing strategies.</p>
              
              <h4 class="font-semibold mb-2">Core Influences</h4>
              <ul class="list-disc pl-6 mb-4 space-y-2">
                <li>Five daily prayer times affect business activity rhythms</li>
                <li>Consumption patterns change significantly during Ramadan (active night economy, surge in food and clothing demand)</li>
                <li>Halal certification becomes a necessary condition for food, cosmetics, and pharmaceuticals to enter the market</li>
              </ul>
              
              <h4 class="font-semibold mb-2">Geographic Distribution</h4>
              <p class="mb-4 leading-relaxed">Java Island and northern Sumatra are traditional Muslim-majority areas, while major cities like Jakarta and Surabaya are more modernized.</p>
            </div>

            <div class="mb-6">
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Christianity (10%)</h3>
              <p class="mb-4 leading-relaxed">Concentrated in Papua, North Sulawesi and other regions, with strong demand for gifts and decorations during Christmas. Christian communities have higher acceptance of Western brands, but localized packaging remains indispensable.</p>
            </div>

            <div class="mb-6">
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Hinduism & Buddhism</h3>
              <p class="mb-4 leading-relaxed"><strong>Bali's Uniqueness:</strong> 90% of Indonesia's Hindus are concentrated in Bali, where the New Year "Nyepi" (Day of Silence) sees the entire island shut down. Enterprises need to plan logistics and operations in advance.</p>
              <p class="mb-4 leading-relaxed"><strong>Chinese Community Ties:</strong> Buddhists largely overlap with Chinese communities, making Lunar New Year and Mid-Autumn Festival key marketing nodes.</p>
            </div>

            <h2 class="text-2xl font-bold mb-4">Festival Economy: Unmissable Growth Windows</h2>
            <div class="mb-6">
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Eid al-Fitr (Hari Raya Puasa)</h3>
              <p class="mb-4 leading-relaxed">This is the biggest festival for Muslims, marking the end of Ramadan. During the festival, Muslim families hold grand reunions and banquets, with market consumption demand increasing significantly. Many merchants launch promotional activities, especially for food, clothing, and electronics.</p>
              
              <div class="bg-yellow-50 p-4 rounded-lg mb-4">
                <h4 class="font-semibold text-yellow-800 mb-2">Festival Marketing Key Points</h4>
                <ul class="list-disc pl-4 text-yellow-700 space-y-1">
                  <li>Start festival marketing preparations 2-3 months in advance</li>
                  <li>Launch special products that align with religious culture</li>
                  <li>Use social media for festival blessing marketing</li>
                  <li>Build good relationships with local religious leaders</li>
                </ul>
              </div>
            </div>

            <div class="mb-6">
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Other Important Festivals</h3>
              <ul class="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Christmas (Hari Natal):</strong> Although Indonesia is a Muslim-majority country, Christmas for Christians still holds important significance</li>
                <li><strong>Eid al-Adha (Hari Raya Haji):</strong> Muslims celebrate Eid al-Adha annually, commemorating Abraham's sacrifice</li>
                <li><strong>Nyepi (Day of Silence):</strong> Bali's Hindu New Year, emphasizing tranquility and introspection</li>
              </ul>
            </div>

            <h2 class="text-2xl font-bold mb-4">Localization Strategy: Avoiding Pitfalls, Seizing Opportunities</h2>
            <div class="mb-6">
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Halal Certification Requirements</h3>
              <p class="mb-4 leading-relaxed">For food, cosmetics, pharmaceuticals and other industries, obtaining Halal certification is a necessary condition for entering the Indonesian market. Indonesia's Halal certification system is relatively strict, requiring enterprises to ensure that the entire process from raw material procurement to production and processing complies with Islamic law requirements.</p>
              
              <div class="bg-red-50 p-4 rounded-lg mb-4">
                <h4 class="font-semibold text-red-800 mb-2">Certification Process Key Points</h4>
                <ul class="list-disc pl-4 text-red-700 space-y-1">
                  <li>Requires review by multiple agencies including BPJPH and MUI</li>
                  <li>Reserve at least 6 months for certification cycle</li>
                  <li>Note the new electronic halal label regulations implemented from 2024</li>
                </ul>
              </div>
            </div>

            <p class="text-gray-600 text-sm mt-8 pt-4 border-t">
              This article is based on authoritative data from Statistics Indonesia, World Bank, McKinsey Southeast Asia Consumer Report, and other sources
            </p>
          </div>
        `
      },
      '9': {
        id: 9,
        title: 'E-commerce Industry Deep Analysis | Southeast Asia E-commerce Market 2024 Transaction Volume Exceeds $120 Billion, Mobile Share Reaches 85%',
        date: '2025-05-18 14:20:15',
        category: 'Industry Insights',
        country: 'All',
        industry: 'E-commerce',
        content: `
          <div class="prose max-w-none">
            <div class="bg-blue-50 p-6 rounded-lg mb-6">
              <h2 class="text-2xl font-bold mb-4 text-blue-900">Market Overview</h2>
              <p class="text-blue-800 leading-relaxed">Southeast Asia's e-commerce market achieved a historic breakthrough in 2024, with total transaction volume reaching $120 billion, up 32% year-on-year. Mobile shopping accounts for 85%, with social commerce and live streaming becoming new growth engines. The market size is expected to exceed $150 billion in 2025. This report provides an in-depth analysis of the current state, growth drivers, and future trends of Southeast Asia's e-commerce market.</p>
            </div>

            <h2 class="text-2xl font-bold mb-4">Market Size and Growth</h2>
            <div class="mb-6">
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Overall Market Performance</h3>
              <p class="mb-4 leading-relaxed">Southeast Asia's e-commerce market total transaction volume reached $120 billion in 2024, up from $91 billion in 2023, representing a 32% increase and setting a new record. This growth is primarily driven by increased mobile internet penetration, convenient digital payments, and the digital transformation of consumer shopping habits.</p>
              
              <div class="grid md:grid-cols-3 gap-4 mb-6">
                <div class="bg-green-50 p-4 rounded-lg">
                  <h4 class="font-semibold text-green-800 mb-2">Total Transaction Volume</h4>
                  <p class="text-2xl font-bold text-green-600">$120 Billion</p>
                  <p class="text-sm text-green-700">32% YoY Growth</p>
                </div>
                <div class="bg-blue-50 p-4 rounded-lg">
                  <h4 class="font-semibold text-blue-800 mb-2">Mobile Share</h4>
                  <p class="text-2xl font-bold text-blue-600">85%</p>
                  <p class="text-sm text-blue-700">8% increase from last year</p>
                </div>
                <div class="bg-purple-50 p-4 rounded-lg">
                  <h4 class="font-semibold text-purple-800 mb-2">Active Users</h4>
                  <p class="text-2xl font-bold text-purple-600">420 Million</p>
                  <p class="text-sm text-purple-700">62% penetration rate</p>
                </div>
              </div>
              
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Country Market Performance</h3>
              <p class="mb-4 leading-relaxed">All six major markets - Indonesia, Thailand, Vietnam, Philippines, Malaysia, and Singapore - achieved double-digit growth, with Indonesia leading at $52 billion in transaction volume, accounting for 43% of the entire Southeast Asian e-commerce market.</p>
              
              <div class="bg-yellow-50 p-4 rounded-lg mb-4">
                <h4 class="font-semibold text-yellow-800 mb-2">Country E-commerce Market Size (2024)</h4>
                <ul class="list-disc pl-4 text-yellow-700 space-y-1">
                  <li>Indonesia: $52 billion (43%)</li>
                  <li>Thailand: $28.5 billion (24%)</li>
                  <li>Vietnam: $16.8 billion (14%)</li>
                  <li>Philippines: $12.5 billion (10%)</li>
                  <li>Malaysia: $7.2 billion (6%)</li>
                  <li>Singapore: $3.0 billion (3%)</li>
                </ul>
              </div>
            </div>

            <h2 class="text-2xl font-bold mb-4">Mobile E-commerce Dominance</h2>
            <div class="mb-6">
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Clear Mobile Advantage</h3>
              <p class="mb-4 leading-relaxed">Mobile e-commerce holds absolute dominance in the Southeast Asian market, accounting for 85% of total transaction volume, significantly higher than the global average of 72%. This phenomenon stems mainly from the leapfrog development of mobile internet in Southeast Asia and the young demographic structure.</p>
              
              <div class="grid md:grid-cols-2 gap-4 mb-6">
                <div class="bg-indigo-50 p-4 rounded-lg">
                  <h4 class="font-semibold text-indigo-800 mb-2">Mobile Advantages</h4>
                  <ul class="list-disc pl-4 text-indigo-700 space-y-1 text-sm">
                    <li>Convenient payment experience</li>
                    <li>Social media integration</li>
                    <li>Personalized recommendation algorithms</li>
                    <li>Real-time customer service support</li>
                  </ul>
                </div>
                <div class="bg-teal-50 p-4 rounded-lg">
                  <h4 class="font-semibold text-teal-800 mb-2">User Behavior Characteristics</h4>
                  <ul class="list-disc pl-4 text-teal-700 space-y-1 text-sm">
                    <li>Average daily usage: 2.5 hours</li>
                    <li>Monthly purchase frequency: 4.2 times</li>
                    <li>Average order value: $45</li>
                    <li>Repeat purchase rate: 68%</li>
                  </ul>
                </div>
              </div>
              
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Mobile App Ecosystem</h3>
              <p class="mb-4 leading-relaxed">Major e-commerce platforms' mobile app downloads continue to grow, with Shopee, Lazada, Tokopedia and other platforms' mobile apps ranking among the top in app stores across countries, with continuously improving user stickiness.</p>
            </div>

            <h2 class="text-2xl font-bold mb-4">Rise of Social Commerce</h2>
            <div class="mb-6">
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Explosive Growth of Social Commerce</h3>
              <p class="mb-4 leading-relaxed">Social commerce achieved explosive growth in 2024, with transaction volume reaching $36 billion, accounting for 30% of the overall e-commerce market. Shopping features on social platforms like Facebook, Instagram, and TikTok are becoming increasingly sophisticated, providing new sales channels for brands and merchants.</p>
              
              <div class="bg-pink-50 p-4 rounded-lg mb-4">
                <h4 class="font-semibold text-pink-800 mb-2">Social Commerce Characteristics</h4>
                <ul class="list-disc pl-4 text-pink-700 space-y-1">
                  <li>Content-driven shopping experience</li>
                  <li>KOL and influencer marketing effects</li>
                  <li>Community-based shopping decisions</li>
                  <li>Instant interaction and feedback</li>
                </ul>
              </div>
              
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Live Streaming Becomes Mainstream</h3>
              <p class="mb-4 leading-relaxed">Live streaming commerce is rapidly popularizing in the Southeast Asian market, with 2024 live streaming e-commerce transaction volume reaching $18 billion, up 150% year-on-year. Major e-commerce platforms have launched live streaming features, with influencers and KOLs becoming important sales channels.</p>
            </div>

            <div class="bg-gray-100 p-6 rounded-lg mb-6">
              <h3 class="text-xl font-semibold mb-3">Conclusion</h3>
              <p class="text-gray-700">Southeast Asia's e-commerce market achieved milestone development in 2024, with $120 billion in transaction volume marking the maturity of the region's digital economy. Trends such as mobile dominance, social commerce rise, and payment innovation will continue to drive market growth. Facing an environment of coexisting challenges and opportunities, e-commerce enterprises need to continue innovating and deepening localized operations to stand out in fierce competition.</p>
            </div>

            <p class="text-gray-600 text-sm mt-8 pt-4 border-t">
              Data sources: Google-Temasek-Bain Southeast Asia Digital Economy Report 2024, national e-commerce associations, major e-commerce platform financial reports
            </p>
          </div>
        `
      },
      '10': {
        id: 10,
        title: 'Fintech Revolution | Digital Payment Penetration in Southeast Asia Reaches 78%, Blockchain Technology Drives Cross-border Remittance Innovation',
        date: '2025-05-10 16:45:30',
        category: 'Industry Insights',
        country: 'All',
        industry: 'Fintech',
        content: `
          <div class="prose max-w-none">
            <div class="bg-blue-50 p-6 rounded-lg mb-6">
              <h2 class="text-2xl font-bold mb-4 text-blue-900">Fintech Revolution Overview</h2>
              <p class="text-blue-800 leading-relaxed">Southeast Asia's fintech industry is experiencing explosive growth, with digital payment penetration reaching 78%, up 45 percentage points from 2020. Blockchain technology applications in cross-border remittances and supply chain finance are becoming increasingly mature, bringing disruptive changes to traditional financial services. This report provides an in-depth analysis of Southeast Asia's fintech development status, technological innovations, and future trends.</p>
            </div>

            <h2 class="text-2xl font-bold mb-4">Rapid Digital Payment Adoption</h2>
            <div class="mb-6">
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Significant Penetration Rate Increase</h3>
              <p class="mb-4 leading-relaxed">Southeast Asia's digital payment penetration reached 78% in 2024, with total users exceeding 520 million and transaction volume reaching $1.8 trillion. Mobile wallets have become the most popular payment method, accounting for 65% of digital payment transactions.</p>
              
              <div class="grid md:grid-cols-3 gap-4 mb-6">
                <div class="bg-green-50 p-4 rounded-lg">
                  <h4 class="font-semibold text-green-800 mb-2">Penetration Rate</h4>
                  <p class="text-2xl font-bold text-green-600">78%</p>
                  <p class="text-sm text-green-700">45% increase from 2020</p>
                </div>
                <div class="bg-blue-50 p-4 rounded-lg">
                  <h4 class="font-semibold text-blue-800 mb-2">Total Users</h4>
                  <p class="text-2xl font-bold text-blue-600">520 Million</p>
                  <p class="text-sm text-blue-700">28% annual growth rate</p>
                </div>
                <div class="bg-purple-50 p-4 rounded-lg">
                  <h4 class="font-semibold text-purple-800 mb-2">Transaction Volume</h4>
                  <p class="text-2xl font-bold text-purple-600">$1.8 Trillion</p>
                  <p class="text-sm text-purple-700">42% YoY growth</p>
                </div>
              </div>
              
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Country Development Levels</h3>
              <p class="mb-4 leading-relaxed">Singapore and Thailand lead in digital payment penetration, reaching 95% and 88% respectively. Indonesia, Vietnam, and Philippines, although starting later, have the fastest growth rates, all exceeding 50% annual growth.</p>
              
              <div class="bg-yellow-50 p-4 rounded-lg mb-4">
                <h4 class="font-semibold text-yellow-800 mb-2">Digital Payment Penetration by Country (2024)</h4>
                <ul class="list-disc pl-4 text-yellow-700 space-y-1">
                  <li>Singapore: 95% (mature market)</li>
                  <li>Thailand: 88% (rapid development)</li>
                  <li>Malaysia: 82% (steady growth)</li>
                  <li>Vietnam: 75% (high-speed growth)</li>
                  <li>Indonesia: 72% (huge potential)</li>
                  <li>Philippines: 68% (fast catching up)</li>
                </ul>
              </div>
            </div>

            <h2 class="text-2xl font-bold mb-4">Blockchain Technology Applications</h2>
            <div class="mb-6">
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Cross-border Remittance Innovation</h3>
              <p class="mb-4 leading-relaxed">Blockchain technology has achieved major breakthroughs in cross-border remittances. Traditional remittances require 3-5 business days, but can now be completed in minutes through blockchain technology. Transaction fees have been reduced from the traditional 8-12% to 2-3%.</p>
              
              <div class="bg-pink-50 p-4 rounded-lg mb-4">
                <h4 class="font-semibold text-pink-800 mb-2">Blockchain Remittance Advantages</h4>
                <ul class="list-disc pl-4 text-pink-700 space-y-1">
                  <li>Transaction time: From 3-5 days to minutes</li>
                  <li>Transaction fees: From 8-12% to 2-3%</li>
                  <li>Transparency: Real-time transaction tracking</li>
                  <li>Security: Encryption technology ensures fund safety</li>
                </ul>
              </div>
              
              <h3 class="text-xl font-semibold mb-3 text-gray-800">• Supply Chain Finance Applications</h3>
              <p class="mb-4 leading-relaxed">Blockchain technology applications in supply chain finance are becoming increasingly mature. Smart contracts automate trade finance processes, improving financing convenience for SMEs and reducing risks for financial institutions.</p>
            </div>

            <div class="bg-gray-100 p-6 rounded-lg mb-6">
              <h3 class="text-xl font-semibold mb-3">Conclusion</h3>
              <p class="text-gray-700">Southeast Asia's fintech industry is experiencing unprecedented development opportunities. The rapid adoption of digital payments, mature applications of blockchain technology, and the rise of digital banks are reshaping the financial services landscape. Facing technological innovation and regulatory challenges, fintech companies need to find balance between innovation and compliance to provide users with safer, more convenient, and inclusive financial services.</p>
            </div>

            <p class="text-gray-600 text-sm mt-8 pt-4 border-t">
              Data sources: Southeast Asia Fintech Association, central banks of various countries, McKinsey Fintech Report 2024, CB Insights
            </p>
          </div>
        `
      }
    };

    if (newsId && newsData[newsId]) {
      setNewsDetail(newsData[newsId]);
    }
  }, [location]);

  if (!newsDetail) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">News Not Found</h2>
          <a href="/en/news" className="text-blue-600 hover:text-blue-700">Back to News List</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#071C74] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="adi-header-left">
            <a href="/">
              <img 
                alt="ADI Logo" 
                width={125} 
                height={56} 
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/3900b388860a5efa532a02080a385ec7.png" 
                className="h-14 w-auto"
              />
            </a>
          </div>
          
          <div className="flex items-center space-x-4 text-white">
            <div className="relative">
              <button 
                className="px-4 py-2 text-white hover:text-blue-200 transition-colors"
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
              >
                {selectedLanguage}
              </button>
              {isLanguageDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50 min-w-[120px]">
                  <a 
                    href="/zh/news/detail"
                    className="block w-full px-4 py-2 text-left hover:bg-gray-100 text-black whitespace-nowrap"
                  >
                    简体中文
                  </a>
                  <button 
                    className="block w-full px-4 py-2 text-left hover:bg-gray-100 text-black whitespace-nowrap"
                    onClick={() => {
                      setSelectedLanguage('English');
                      setIsLanguageDropdownOpen(false);
                    }}
                  >
                    English
                  </button>
                </div>
              )}
            </div>
            
            <a 
              href="/flanexus/en"
              className="px-4 py-2 text-white hover:text-blue-200 transition-colors whitespace-nowrap"
            >
              Flanexus
            </a>
            
            <a 
              href="/en/news"
              className="px-4 py-2 text-white hover:text-blue-200 transition-colors"
            >
              News
            </a>
            
            <a 
              href="/en/contact-us"
              className="px-4 py-2 border border-white rounded-full hover:bg-white hover:text-[#071C74] transition-colors text-white"
            >
              Contact Us
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <a href="/en" className="hover:text-blue-600">Home</a>
            <span>/</span>
            <a href="/en/news" className="hover:text-blue-600">News</a>
            <span>/</span>
            <span className="text-gray-900">Detail</span>
          </div>
        </nav>

        {/* Article Header */}
        <div className="border-b border-gray-300 pb-6 mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
            {newsDetail.title}
          </h1>
          <div className="flex items-center justify-between">
            <p className="text-gray-500">{newsDetail.date}</p>
            <div className="flex items-center space-x-2">
              {newsDetail.category === 'Industry Insights' ? (
                <span className="px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded-full">
                  {newsDetail.industry || 'All'}
                </span>
              ) : (
                <span className="px-3 py-1 bg-blue-100 text-blue-600 text-sm rounded-full">
                  {newsDetail.country}
                </span>
              )}
              <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                {newsDetail.category}
              </span>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div 
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: newsDetail.content }}
        />

        {/* Back Button */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <a 
            href="/en/news"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to News List
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="flex h-28 items-center justify-center bg-[#F7F7F7] mt-16">
        <span className="text-lg font-semibold text-black/85">Strategic Partner: </span>
        <a 
          href="https://flanexus.cn/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="ml-1 text-lg font-semibold underline decoration-[#2D66F4] text-[#2D66F4] hover:text-blue-700"
        >
          Flanexus
        </a>
        <div className="ml-8">
          <a 
            href="https://readdy.ai/?origin=logo" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Powerd by AI
          </a>
        </div>
      </footer>
    </div>
  );
};

export default NewsDetailEn;
